import React from "react"
import { ZoneType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { DropdownMenuSeparator } from "@radix-ui/react-dropdown-menu"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { editZoneType } from "@/lib/api/zone"
import { useToast } from "@/hooks/use-toast"

import { Icons } from "../icons"
import { Button } from "../ui/button"
import { Card } from "../ui/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form"
import { Input } from "../ui/input"
import { Textarea } from "../ui/textarea"

const formSchema = z.object({
  zone_name: z.string().min(1, "Zone name is required"),
  zone_code: z.string().min(1, "Zone code is required"),
  meter_reading_schedule: z.coerce
    .number({
      required_error: "Meter reading schedule is required",
      invalid_type_error: "Meter reading schedule must be a number",
    })
    .min(1, { message: "Meter reading schedule must be between 1 and 31" })
    .max(31, { message: "Meter reading schedule must be between 1 and 31" }),
  zone_description: z.string().min(1, "Zone description is required"),
  zone_capacity: z.coerce.number({
    required_error: "Zone capacity is required",
    invalid_type_error: "Zone capacity must be a number",
  }).min(1, "Zone capacity must be greater than 0"),
  zone_status: z.string().min(1, "Zone status is required"),
})
export default function EditZonesForm({
  zoneType,
  setShowEditZoneTypeModal,
}: {
  zoneType: ZoneType
  setShowEditZoneTypeModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { toast } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      zone_name: zoneType?.zone_name || "",
      zone_code: zoneType?.zone_code || "",
      meter_reading_schedule: zoneType?.meter_reading_schedule || 0,
      zone_description: zoneType?.zone_description || "",
      zone_capacity: zoneType?.zone_capacity || 0,
      zone_status: zoneType?.zone_status || "",
    },
  })

  React.useEffect(() => {
    if (zoneType) {
      form.reset({
        zone_name: zoneType.zone_name,
        zone_code: zoneType.zone_code,
        meter_reading_schedule: zoneType.meter_reading_schedule,
        zone_description: zoneType.zone_description,
        zone_capacity: zoneType.zone_capacity,
        zone_status: zoneType.zone_status,
      })
    }
  }, [zoneType, form.reset])

  const mutation = useMutation({
    mutationFn: async (data: ZoneType) => {
      if (zoneType?.zone_id) {
        await editZoneType(zoneType?.zone_id, data)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["zonesDetails"],
      })
      toast({
        description: "Zone Type updated successfully",
      })
      setShowEditZoneTypeModal(false)
    },
    onError: () => {
      toast({
        title: "Something went wrong",
        description: "Please try again",
        variant: "destructive",
      })
    },
  })

  function handleSubmit(values: z.infer<typeof formSchema>) {
    console.log("Submitting values:", values)
    mutation.mutate(values as ZoneType)
  }
  return (
    <div className="px-2">
      <DropdownMenuSeparator className="mt-4 h-[1px] bg-muted" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="grid grid-cols-1 gap-4 md:max-w-[1000px]"
        >
          <Card className="my-4 gap-4 p-6">
            {/* Zone Name and Zone Code */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="zone_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Zone Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="zone_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Zone Code <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {/* Meter Reading Schedule */}
            <FormField
              control={form.control}
              name="meter_reading_schedule"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Meter Reading Schedule{" "}
                    <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Zone Description */}
            <FormField
              control={form.control}
              name="zone_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Zone Description <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea className="resize-none" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Zone Capacity and Zone Status */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="zone_capacity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Zone Capacity <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="zone_status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Zone Status <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </Card>
          <div className="flex flex-row justify-end gap-2">
            <Button
              className="w-28"
              variant="outline"
              type="button"
              onClick={() => {
                setShowEditZoneTypeModal(false)
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              className="w-28"
            >
              {mutation.isPending && (
                <Icons.loader className="mr-2 animate-spin" />
              )}
              Save Changes
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
